import Analytics, { Events } from 'analytics/Analytics'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useAuth } from 'components/providers/AuthProvider'
import { ReactNode, useState } from 'react'
import { logout } from 'services/api/auth'

import * as Styled from '../styled'
import { t } from 'i18next'
import { AccountDoc, UserSettingsDoc } from 'types/api/changes'
import { Model } from 'types/model'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { deleteAccount, updateAccountOnServer } from 'services/api/account'
import DeleteAccountDialog from 'components/dialogs/DeleteAccountDialog'
import { MenuDeleteAccIcon, MenuLogoutIcon } from 'assets/icons/menu'
import { isTestEnv } from 'utils/env'
import { Account } from 'services/db/Queries/AccountQueries'
import Database from 'services/db/Database'
import i18n from 'i18n'
import Logger from 'services/Logger'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import { updateUserSettings } from 'services/api/userSettings'

interface AccountActionProps {
  open: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  userSettings?: Model<UserSettingsDoc>
  account?: Model<AccountDoc>
}

function Content(props: AccountActionProps) {
  const auth = useAuth()

  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const performLogout = () => {
    logout()
      .then(() => {
        return auth.onLogout()
      })
      .then(() => {
        Analytics.getInstance().sendEvent(Events.LOGOUT)
      })
      .catch((error) => {
        console.error('logout error', error)
        auth.onLogout()
      })
  }

  const performDeleteAccount = () => {
    deleteAccount(props.account?._id ?? '').then(() => {
      return performLogout()
    })
  }

  return (
    <PopupSection title={`${t('account')}`} noBottomBorder>
      <PopupSectionRow
        icon={<MenuLogoutIcon />}
        title={t('logout')}
        action={performLogout}
        actionId="logout-button"
        actionBg
        engulfEntireRow
        onlyTitle
      />
      <PopupSectionRow
        titleColor="var(--color-negative)"
        title={t('deleteAccount')}
        icon={<MenuDeleteAccIcon />}
        action={() => {
          setShowDeleteAccountDialog(true)
        }}
        actionBg
        engulfEntireRow
        onlyTitle
      />
      {showDeleteAccountDialog && (
        <ConfirmationDialog
          leftButtonColor="black"
          title={t('areYouSureDeleteAccount')}
          content={t('onceDeleted')}
          leftButton={`${t('no')}`}
          rightButton={`${t('yes')}`}
          handleLeftButton={() => {
            setShowDeleteAccountDialog(false)
          }}
          handleRightButton={() => {
            setShowDeleteAccountDialog(false)
            setShowDeleteConfirm(true)
          }}
          open={showDeleteAccountDialog}
        />
      )}
      {isTestEnv() && (
        <PopupSectionRow
          title={'Remove purchases'}
          onlyTitle
          actionBg
          engulfEntireRow
          actionId="remove-purchases-button"
          action={() => {
            Account().then((account) => {
              updateAccountOnServer({
                ...(Database.toServerObject(account) as AccountDoc),
                Products: []
              }).then((response) => {
                Logger.blue('Removed purchases:', response.data)
                props.setOpen(false)

                Database.write(async () => {
                  account.update((acc) => {
                    acc._raw['_rev'] = response.data.rev
                    acc._raw['Products'] = []
                  })
                })
              })
            })
          }}
        />
      )}
      {isTestEnv() && (
        <PopupSectionRow
          title={'Remove Connected personal calendars'}
          onlyTitle
          actionBg
          engulfEntireRow
          actionId="remove-connected-calendars-button"
          action={() => {
            Account().then((account) => {
              Database.write(async () => {
                account.update((acc) => {
                  acc.ConnectedCalAccounts = {}
                })
              })
              updateAccountOnServer({
                ...(Database.toServerObject(account) as AccountDoc),
                ConnectedCalAccounts: {}
              }).then(() => {
                props.setOpen(false)
              })
            })
          }}
        />
      )}
      {isTestEnv() && (
        <PopupSectionRow
          title={'Reset groups settings'}
          onlyTitle
          actionBg
          actionId="reset-groups-settings-button"
          action={() => {
            UserSettings().then((us) => {
              Database.write(async () => {
                us.update((acc) => {
                  acc.GroupsSettings = {}
                })
              })
              updateUserSettings({
                ...(Database.toServerObject(us) as UserSettingsDoc),
                GroupsSettings: {}
              }).then(() => {
                props.setOpen(false)
              })
            })
          }}
        />
      )}
      {showDeleteConfirm && (
        <DeleteAccountDialog
          delete={performDeleteAccount}
          setOpen={setShowDeleteConfirm}
          open={showDeleteConfirm}
        />
      )}
    </PopupSection>
  )
}

export default function AccountActionsPopup(props: AccountActionProps) {
  return (
    <Styled.UserMenuPopup
      open={props.open}
      setOpen={props.setOpen}
      placement={i18n.dir() === 'ltr' ? 'right' : 'left'}
      fallbackPlacements={
        i18n.dir() === 'ltr' ? ['right', 'left', 'top'] : ['left', 'top']
      }
      content={<Content {...props} />}
    >
      {props.children}
    </Styled.UserMenuPopup>
  )
}
